import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isMobileMenuOpen]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-background-50/95 backdrop-blur-md shadow-sm"
            : "bg-transparent"
        }`}
      >
        <nav className="w-full px-4 md:px-8 h-[72px] flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 text-foreground-900 hover:text-primary-500 transition-colors cursor-pointer">
            <span className="font-heading text-xl font-bold whitespace-nowrap">hipobuy sheet</span>
          </a>

          <div className="hidden md:flex items-center gap-8">
            <a href="/" className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">Home</a>

            <div
              className="relative"
              onMouseEnter={() => setIsDropdownOpen(true)}
              onMouseLeave={() => setIsDropdownOpen(false)}
            >
              <button className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
                Categories
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-[240px] bg-background-50 rounded-xl border border-background-200 shadow-lg py-2 z-50">
                  <div className="px-4 py-1.5 text-xs font-semibold text-foreground-400 uppercase tracking-wider">All Categories</div>
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors whitespace-nowrap"
                    >
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className={cat.icon}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link to="/hipobuy-spreadsheet-guide" className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap">Guide</Link>
            <Link to="/best-hipobuy-finds-2026" className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap">Finds</Link>
            <Link to="/hipobuy-spreadsheet-comparison" className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap">Compare</Link>
            <a href="#trust" className="text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">Trust</a>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="bg-foreground-900 text-background-50 px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
            >
              hipobuy sheet
            </a>

            <button
              className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-900 cursor-pointer relative z-50"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            >
              <div className="w-5 h-4 relative flex flex-col justify-between">
                <span className={`block h-[2px] w-full bg-current rounded-full transition-all duration-300 origin-center ${isMobileMenuOpen ? "rotate-45 translate-y-[7px]" : ""}`}></span>
                <span className={`block h-[2px] w-full bg-current rounded-full transition-all duration-200 ${isMobileMenuOpen ? "opacity-0 scale-x-0" : ""}`}></span>
                <span className={`block h-[2px] w-full bg-current rounded-full transition-all duration-300 origin-center ${isMobileMenuOpen ? "-rotate-45 -translate-y-[7px]" : ""}`}></span>
              </div>
            </button>
          </div>
        </nav>
      </header>

      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-foreground-900/40 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>

          <div className="absolute top-0 right-0 w-[85%] max-w-[380px] h-full bg-background-50 overflow-y-auto shadow-2xl animate-slide-in-right">
            <div className="pt-[72px] pb-8">
              <div className="px-5 mb-6">
                <Link
                  to="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-900 hover:bg-background-100 transition-colors"
                >
                  <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                    <i className="ri-home-4-line text-primary-500"></i>
                  </span>
                  <span className="font-semibold text-base">Home</span>
                </Link>
              </div>

              <div className="px-5 mb-6">
                <div className="flex items-center gap-2 px-4 mb-3">
                  <span className="w-1 h-4 rounded-full bg-accent-500"></span>
                  <span className="text-xs font-semibold text-foreground-400 uppercase tracking-wider">Product Categories</span>
                </div>
                <div className="space-y-0.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-all"
                    >
                      <span className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                        <i className={`${cat.icon} text-foreground-500`}></i>
                      </span>
                      <span className="font-medium">{cat.name}</span>
                      {cat.featured && (
                        <span className="ml-auto text-[10px] font-semibold bg-accent-100 text-accent-600 px-2 py-0.5 rounded-full">Hot</span>
                      )}
                    </a>
                  ))}
                </div>
              </div>

              <div className="border-t border-background-200 mx-5 mb-6"></div>

              <div className="px-5 mb-6">
                <div className="flex items-center gap-2 px-4 mb-3">
                  <span className="w-1 h-4 rounded-full bg-secondary-500"></span>
                  <span className="text-xs font-semibold text-foreground-400 uppercase tracking-wider">Guides & Resources</span>
                </div>
                <div className="space-y-0.5">
                  {[
                    { to: "/hipobuy-spreadsheet-guide", icon: "ri-book-open-line", label: "Spreadsheet Guide", desc: "Complete reference" },
                    { to: "/best-hipobuy-finds-2026", icon: "ri-sparkling-line", label: "Best Finds 2026", desc: "Curated discoveries" },
                    { to: "/hipobuy-spreadsheet-comparison", icon: "ri-scales-line", label: "Platform Comparison", desc: "Side-by-side analysis" },
                    { to: "/hipobuy-spreadsheet-beginners", icon: "ri-seedling-line", label: "Beginner Tutorial", desc: "Start from zero" },
                  ].map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 transition-all"
                    >
                      <span className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-foreground-500`}></i>
                      </span>
                      <div className="flex flex-col">
                        <span className="font-medium text-foreground-900">{item.label}</span>
                        <span className="text-xs text-foreground-400">{item.desc}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="border-t border-background-200 mx-5 mb-6"></div>

              <div className="px-5">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-2 mx-4 py-3.5 rounded-xl bg-foreground-900 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-all cursor-pointer"
                >
                  <i className="ri-shopping-bag-3-line"></i>
                  Visit Main Website
                  <i className="ri-arrow-right-up-line text-xs"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>
    </>
  );
}